import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import { AppBar, Box, Button, Dialog, IconButton, Toolbar, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SlideUpTransition from 'components/shared/transitions/SlideUpTransition';
import ContentRenderer from 'components/shared/content/ContentRenderer';
import StyledH2Title from 'common/theme/shared/StyledH2Title';
import StyledNewsDate from 'common/theme/news-view/StyledNewsDate';
import StyledNewsImageContainer from 'common/theme/news-view/StyledNewsImageContainer';
import StyledNewsImage from 'common/theme/news-view/StyledNewsImage';
import StyledNewsBodyContainer from 'common/theme/news-view/StyledNewsBodyContainer';
import { dateFormat } from 'common/constants/date';

/**
 * Vista previa a pantalla completa del artículo (noticia o T&C) EXACTAMENTE como lo
 * verá el usuario final: mismo layout y componentes de NewsView (columna centrada,
 * título, fecha, imagen destacada y cuerpo con ContentRenderer). Funciona con lo que
 * llevas escrito en el formulario, SIN necesidad de guardar.
 */
function ContentPreviewDialog({ open, onClose, title = '', date = null, imageUrl = null, body = '' }) {
  const { t, i18n } = useTranslation(['buttons']);
  const dateLocale = i18n.language === 'es' ? es : enUS;

  let formattedDate = null;
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    formattedDate = format(date, dateFormat, { locale: dateLocale });
  }

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={SlideUpTransition}
      PaperProps={{ sx: { backgroundColor: 'background.default', backgroundImage: 'none' } }}>
      <AppBar
        color="transparent"
        sx={{ position: 'sticky', borderBottom: '1px solid', borderColor: 'hairline.main', boxShadow: 'none', backgroundColor: 'surface.raised' }}>
        <Toolbar>
          <VisibilityOutlinedIcon sx={{ color: 'gold.main', mr: 1.25 }} />
          <Typography variant="h6" sx={{ flex: 1, color: 'text.primary', fontWeight: 800 }}>
            {t('preview', { ns: 'buttons', defaultValue: 'Vista previa' })}
          </Typography>
          <IconButton
            edge="end"
            onClick={onClose}
            aria-label={t('close', { ns: 'buttons', defaultValue: 'Cerrar' })}
            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, sm: 4 } }}>
        {/* Misma columna de artículo que NewsView. */}
        <Box sx={{ maxWidth: 760, mx: 'auto', width: '100%' }}>
          {title && <StyledH2Title component="h2">{title}</StyledH2Title>}
          {formattedDate && (
            <StyledNewsDate color="secondary" variant="body1">{formattedDate}</StyledNewsDate>
          )}
          {imageUrl && (
            <StyledNewsImageContainer>
              <StyledNewsImage src={imageUrl} alt={title || ''} />
            </StyledNewsImageContainer>
          )}
          <StyledNewsBodyContainer>
            {body ? (
              <ContentRenderer value={body} />
            ) : (
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                {t('previewEmpty', { ns: 'buttons', defaultValue: 'Aún no has escrito contenido.' })}
              </Typography>
            )}
          </StyledNewsBodyContainer>
        </Box>
      </Box>
    </Dialog>
  );
}

ContentPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  date: PropTypes.instanceOf(Date),
  imageUrl: PropTypes.string,
  body: PropTypes.string,
};

/**
 * Normaliza el valor del campo de imagen del formulario a una URL mostrable:
 * string (ya guardada) o File del cropper (con .preview).
 */
export const previewImageUrl = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value.preview || null;
};

/**
 * Botón "Vista previa" autocontenido (abre el ContentPreviewDialog con los values
 * actuales del formulario). Se usa en crear/editar Noticias y T&C.
 */
export function ContentPreviewAction({ title = '', date = null, imageUrl = null, body = '' }) {
  const { t } = useTranslation(['buttons']);
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<VisibilityOutlinedIcon />}
        onClick={() => setOpen(true)}
        sx={{
          mr: 4,
          color: 'text.primary',
          borderColor: 'hairline.strong',
          fontWeight: 700,
          textTransform: 'none',
          '&:hover': { borderColor: 'text.disabled', background: 'rgba(255,255,255,0.06)' },
        }}>
        {t('preview', { ns: 'buttons', defaultValue: 'Vista previa' })}
      </Button>
      <ContentPreviewDialog
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        date={date}
        imageUrl={imageUrl}
        body={body}
      />
    </>
  );
}

ContentPreviewAction.propTypes = {
  title: PropTypes.string,
  date: PropTypes.instanceOf(Date),
  imageUrl: PropTypes.string,
  body: PropTypes.string,
};

export default ContentPreviewDialog;
